import { processContent, bindingMode, autoinject, TaskQueue, observable, computedFrom } from 'aurelia-framework';
import { fireEvent } from 'app/shared/lib/fire-events';
import { bindable } from 'aurelia-typed-observable-plugin';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { getKey, Key } from 'app/shared/lib/keys';
import { changeTag } from 'app/shared/lib/dom';
import { stop } from 'app/shared/lib/noop';
import { IInput } from 'app/shared/elements/forms/input-base';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';

@autoinject()
@processContent(EssAutocompleteCustomElement.processContent)
export class EssAutocompleteCustomElement implements IInput {

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public value: any | null = null;

    @bindable.string()
    public testName: string = '';

    // INFO [Matt] bindable.stringNull({ defaultBindingMode: bindingMode.twoWay }) doesn't work!
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public query: string = '';

    @bindable.stringNull()
    public label: string | null = null;

    @bindable.stringNull()
    public requiredValidatorLabel: string | null = null;

    @bindable.string()
    public autocomplete: string = 'off';

    @bindable.stringNull()
    public placeholder: string | null = null;

    @bindable.boolean()
    public required: boolean = false;

    @bindable.boolean()
    public readonly: boolean = false;

    @bindable.boolean()
    public disabled: boolean = false;

    @bindable.stringNull()
    protected labelledbyId: string | null = null;

    @bindable.stringNull()
    public arialabelValue: string | null = null;

    @observable() protected activeIndex = -1;

    public inputElement!: HTMLInputElement;
    public labelElement!: HTMLLabelElement;
    protected $displayOptions!: HTMLElement;

    protected stop = stop;
    protected display: string = '';
    protected $options!: HTMLElement;
    protected resultCount: number = 0;
    protected id: guid = guid();
    protected isExpanded: boolean = false;

    constructor(private readonly i18n: I18N, protected element: Element, protected queue: TaskQueue) {
        // add focus method to custom element
        (this.element as any).focus = () => this.inputElement.focus();
    }

    protected attached() {
        let observer = new MutationObserver(() => {
            if(this.$displayOptions) this.$displayOptions.innerHTML = '';
            this.resultCount = this.$options?.children?.length || 0;
            if(!!this.resultCount) {
                for(let option of this.$options.children) {
                    this.$displayOptions.appendChild(option.cloneNode(true));
                }
            }
            this.highlightMatches();
        });
        observer.observe(this.$options, { childList: true, subtree: true, characterData: true, attributes: true });
        this.placeholderChanged();
        this.updateAriaExpanded();
    }

    protected handleFocusout() {
        this.updateAriaExpanded();
    }

    private updateAriaExpanded() {
        if(this.inputElement && this.$displayOptions) {
            this.isExpanded = this.inputElement.matches(':focus-within') && !this.$displayOptions.matches('.collection:empty');
        }
    }

    protected updateDropdownPosition() {
        if(this.$displayOptions) {
            const { left, top, bottom, width, height } = this.element.getBoundingClientRect();
            this.$displayOptions.style.setProperty('left', `${left}px`);
            if(2 * top + height < window.innerHeight) { // down
                this.$displayOptions.style.setProperty('top', `${top + height}px`);
                this.$displayOptions.style.setProperty('bottom', null);
            } else { // up
                this.$displayOptions.style.setProperty('top', null);
                this.$displayOptions.style.setProperty('bottom', `calc(100vh - ${bottom - height}px)`);
            }
            this.$displayOptions.style.setProperty('width', `${width}px`);
        }
        this.updateAriaExpanded();
    }

    protected queryChanged() {
        this.activeIndex = -1;
        this.display = '';
        this.updateDropdownPosition();
    }

    protected activeIndexChanged(index: number) {
        if(!this.$displayOptions) {
            return;
        }
        Array.from(this.$displayOptions.children).forEach((item) => item.classList.remove('active'));
        const item = this.$displayOptions.children[index];
        if(item) {
            const element = <HTMLElement>item;
            element.scrollIntoView({ block: 'nearest' });
            this.display = element.innerText.trim();
            item.classList.add('active');
        } else {
            this.display = '';
        }
    }

    protected highlightMatches() {
        const re = new RegExp(this.query, 'i');
        if(!!this.$displayOptions?.children?.length) {
            for(let option of this.$displayOptions.children) {
                option.innerHTML = (<HTMLElement>option).innerText.replace(re, '<b>$&</b>');
            }
        }
        this.updateAriaExpanded();
    }

    protected select(index: number) {
        const item = this.$options.children[index] as any;
        if(!item) {
            return;
        }
        this.value = item.value;
        this.display = item.innerText.trim();
        this.$displayOptions.innerHTML = '';
        this.queue.queueTask(() => fireEvent(this.element, 'change', this.value));
    }

    public clear(): void {
        this.value = null;
        this.query = '';
        this.display = '';
    }

    // INFO [Matt] We don't have the individual list items available in the view
    // so we need to attach the handler to the list and find the item manually.
    protected handleClick({ target }: MouseEvent) {
        const item = (<any>target).closest('.collection-item');
        const index = Array.from(this.$displayOptions.children).indexOf(item);
        this.select(index);
    }

    protected handleMouseover({ target }: MouseEvent) {
        const item = (<any>target).closest('.collection-item');
        this.activeIndex = Array.from(this.$displayOptions.children).indexOf(item);
    }

    protected handleKeydown(event: KeyboardEvent): true | void {
        if(this.readonly) { return true }

        switch(getKey(event)) {

            // Move hover down in the list.
            case Key.Down:
                this.activeIndex = Math.min(this.activeIndex + 1, this.$displayOptions.children.length - 1);
                break;

            // Move hover up in the list.
            case Key.Up:
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
                break;

            // Unhover the list.
            case Key.Esc:
                if(this.query) {
                    this.query = '';
                    this.activeIndex = -1;
                    fireEvent(this.element, 'input');
                    event.stopPropagation();
                }
                break;

            // Select the hovered item.
            case Key.Enter:
            case Key.Space:
                if(this.activeIndex >= 0) {
                    this.select(this.activeIndex);
                    return;
                }
                return true;

            // Don't handle.
            default:
                return true;
        }
    }

    static processContent(_a: any, _b: any, el: Element, _d: any): true {

        // Transform <ess-option> into <a class="collection-item">
        const options = el.querySelectorAll('ess-option');
        options.forEach((opt) => {
            opt = changeTag(opt, 'a');
            opt.classList.add('collection-item');
            opt.setAttribute("role", "option");
        });

        return true;
    }

    //code moved from ess-text

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    protected placeholderChanged(): void {
        if(!this.placeholder) {
            this.inputElement.removeAttribute('placeholder');
        }
        this.queueUpdateLabel();
    }

    private queueUpdateLabel(): void {
        this.queue.queueTask(() => {
            updateMaterializeInputLabel(this.inputElement, this.labelElement);
        });
    }
}
